import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DaySelector } from "../components/DaySelector";
import { ErrorState } from "../components/ErrorState";
import { Footer } from "../components/Footer";
import { Icon } from "../components/Icon";
import { ListHeader } from "../components/ListHeader";
import { MapDrawer } from "../components/MapDrawer";
import { MapSpotCard, SkeletonMapSpotCard } from "../components/MapSpotCard";
import { MapDayTag } from "../components/map/MapDayTag";
import { MapSkeleton } from "../components/map/MapSkeleton";
import { SpotsMap } from "../components/map/SpotsMap";
import { Navbar } from "../components/Navbar";
import { SearchModal } from "../components/SearchModal";
import { SkeletonSpotCard, SpotCard } from "../components/SpotCard";
import { useForecast } from "../lib/api";
import { useCustomLocationForecast } from "../lib/customLocation";
import { useUnit } from "../lib/hooks";
import { regionById } from "../lib/regions";
import { useOverviewScrollRestore } from "../lib/scrollRestoration";
import { ScrollSyncProvider } from "../lib/scrollSync";
import { STATIC_SPOTS } from "../lib/staticSpots";
import type { Coords, EnrichedSpot, Region } from "../lib/types";
import { useMeta } from "../lib/useMeta";
import { comparePeakKey, longDayLabel, type PeakKey, peakHourKey } from "../lib/utils";
import styles from "./OverviewPage.module.css";

type SortMode = "best-today" | "best-week" | "by-area" | "alpha";

const SORT_MODES: ReadonlySet<SortMode> = new Set(["best-today", "best-week", "by-area", "alpha"]);

// SessionStorage key for the filter snapshot. Keyed by region so each
// region remembers its own filter independently. Restored on every
// OverviewPage mount — sessionStorage is per-tab + per-region, so the
// "stick to my last choice" behaviour is the natural expectation for
// the user navigating back from a spot detail and any other entry to
// the same region within the same tab. Fresh tab = fresh state.
const FILTER_STATE_PREFIX = "__bw:overviewFilter:";

interface PersistedFilterState {
  sortMode: SortMode;
  selectedDay: number | null;
}

function filterKey(regionId: string): string {
  return `${FILTER_STATE_PREFIX}${regionId}`;
}

function readPersistedFilter(regionId: string): PersistedFilterState | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(filterKey(regionId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedFilterState>;
    if (!parsed.sortMode || !SORT_MODES.has(parsed.sortMode)) return null;
    const day = parsed.selectedDay;
    const validDay = day === null || (typeof day === "number" && Number.isFinite(day));
    if (!validDay) return null;
    return {
      sortMode: parsed.sortMode,
      selectedDay: day ?? null,
    };
  } catch {
    return null;
  }
}

function writePersistedFilter(regionId: string, state: PersistedFilterState): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(filterKey(regionId), JSON.stringify(state));
}

interface SpotSection {
  /** `null` for a flat (header-less) list. */
  header: string | null;
  spots: EnrichedSpot[];
}

/**
 * Find the forecast day whose peak `(score, speed)` across all spots
 * is highest. The composite key (score primary, peak speed secondary)
 * prevents the global peak from silently collapsing to the earliest
 * day when the scorer caps several days at the same bucket value.
 */
function globalPeakDayIndex(spots: EnrichedSpot[]): number | null {
  const dayCount = spots[0]?.days.length ?? 0;
  if (dayCount === 0) return null;
  let bestDay = 0;
  let bestKey: PeakKey = [-1, -1];
  for (let d = 0; d < dayCount; d++) {
    let dayKey: PeakKey = [0, 0];
    for (const spot of spots) {
      const k = peakHourKey(spot, d);
      if (comparePeakKey(k, dayKey) < 0) dayKey = k;
    }
    if (comparePeakKey(dayKey, bestKey) < 0) {
      bestKey = dayKey;
      bestDay = d;
    }
  }
  return bestKey[0] > 0 ? bestDay : null;
}

function buildSections(
  spots: EnrichedSpot[],
  mode: SortMode,
  selectedDay: number | null,
): SpotSection[] {
  const arr = [...spots];

  if (mode === "best-today") {
    // Flat list, ranked by the spot's peak (score, speed) on the
    // strip-selected day (or today by default). Dropdown label updates
    // as the strip moves so the user sees what window they're looking
    // at — see `bestTodayDropdownLabel()` below.
    const dayIndex = selectedDay ?? 0;
    arr.sort((a, b) => {
      const diff = comparePeakKey(peakHourKey(a, dayIndex), peakHourKey(b, dayIndex));
      if (diff !== 0) return diff;
      const priorityDiff = b.priority - a.priority;
      if (priorityDiff !== 0) return priorityDiff;
      return a.name.localeCompare(b.name);
    });
    return [{ header: null, spots: arr }];
  }

  if (mode === "best-week") {
    return buildBestWeekSections(arr);
  }

  if (mode === "by-area") {
    arr.sort((a, b) => {
      const areaDiff = a.area.localeCompare(b.area);
      return areaDiff !== 0 ? areaDiff : a.name.localeCompare(b.name);
    });
    return [{ header: null, spots: arr }];
  }

  // alpha
  arr.sort((a, b) => a.name.localeCompare(b.name));
  return [{ header: null, spots: arr }];
}

/**
 * Two sections: the top 1–3 spots on the week's peak day under a
 * "Best [Day Date]" header, and the rest under "Other spots". Both
 * sections rank by `(peakHourKey)` on that peak day, so the order is
 * a single coherent best-to-worst list split into a highlight tier
 * and the long tail — no "second best" carve-out, per spec.
 */
function buildBestWeekSections(spots: EnrichedSpot[]): SpotSection[] {
  if (spots.length === 0) return [];
  // Find the day with the highest peak (score, speed) across all
  // spots — the composite key (score primary, peak speed secondary)
  // prevents the global peak from silently winning by earliest-on-tie
  // at the scorer's bucketed score ceiling.
  const peak = globalPeakDayIndex(spots);
  const bestDay = peak ?? -1;
  const bestDate = bestDay >= 0 ? spots[0]?.days[bestDay]?.date : undefined;
  if (bestDay < 0 || !bestDate) {
    // No data at all — fall back to a flat alphabetical list so the UI
    // still renders something predictable.
    return [
      {
        header: null,
        spots: [...spots].sort((a, b) => a.name.localeCompare(b.name)),
      },
    ];
  }

  const ranked = [...spots].sort((a, b) =>
    comparePeakKey(peakHourKey(a, bestDay), peakHourKey(b, bestDay)),
  );
  const top = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  const sections: SpotSection[] = [
    {
      header: `Best ${longDayLabel(bestDay, bestDate)}`,
      spots: top,
    },
  ];
  if (rest.length > 0) {
    sections.push({ header: "Other spots", spots: rest });
  }
  return sections;
}

/** Dynamic label for the best-today dropdown option. */
function bestTodayDropdownLabel(selectedDay: number | null, refDate: string | undefined): string {
  if (!refDate) return "Best wind today";
  const dayIndex = selectedDay ?? 0;
  return `Best wind ${longDayLabel(dayIndex, refDate)}`;
}

const SKELETON_COUNT = 7;

interface OverviewPageProps {
  /** Comes from wouter route params (`/region/:regionId`). */
  params: { regionId: string };
}

/**
 * Spots overview filtered to one region. Layout: 50/50 list+map split at
 * xl, drawer-over-map at mobile. Selecting a marker on the side map
 * opens a floating MapSpotCard at the bottom of the map (decoupled from
 * the drawer — the drawer stays at peek + plain page-scroll the way
 * RegionsPage does). On mobile the drawer is hidden while a spot is
 * selected so the map + MapSpotCard get the full screen.
 */
export function OverviewPage({ params }: OverviewPageProps) {
  const [location, setLocation] = useLocation();
  // Restore scroll if user just came back from a /spot/:id page; else
  // scroll to top. Continuously saves scroll while on this page.
  useOverviewScrollRestore(params.regionId, location);
  const { data, isError, refetch } = useForecast();
  const { unit, cycleUnit } = useUnit();
  const [searchOpen, setSearchOpen] = useState(false);
  // Persisted filter snapshot — restored once on mount so the user's
  // last filter + day pick survives a round-trip to a spot detail
  // (or any other navigation within the same tab). Captured once via
  // `useMemo` so both state hooks below see the same snapshot.
  const restoredFilter = useMemo(() => readPersistedFilter(params.regionId), [params.regionId]);

  // Day driving the map markers AND the selected/dimmed state on each
  // SpotCard's forecast row. Starts at 0 (today) so the strip always has
  // a column highlighted — "Best wind today" reads cleaner when today
  // is the visibly-active day, not just an implicit default. Tapping
  // another day in the strip switches to it; tapping the active day is
  // a no-op (no clear-to-null behavior — every mode wants a definite
  // day to anchor the map and per-card forecast highlights). `null` is
  // kept as a permitted type because legacy props elsewhere treated it
  // that way; new code should rely on it always being a number here.
  const [selectedDay, setSelectedDay] = useState<number | null>(
    () => restoredFilter?.selectedDay ?? 0,
  );
  // Sort mode for the spot list. Default "best-today" ranks by best
  // remaining hour today. Each mode reads the strip differently — see
  // `handleSelectDay` below.
  const [sortMode, setSortMode] = useState<SortMode>(
    () => restoredFilter?.sortMode ?? "best-today",
  );
  const handleSelectDay = (dayOffset: number) => {
    captureFlipPositions();
    // best-week is anchored to the global peak day — once the user picks
    // a *different* day in the strip they're no longer reviewing "best
    // wind this week", so switch the sort to "best-today" for that day.
    // The dropdown label updates dynamically ("Best wind Friday 22nd").
    if (sortMode === "best-week") {
      setSelectedDay(dayOffset);
      setSortMode("best-today");
      return;
    }
    // Other modes: always pin to the tapped day. No toggle-off — the
    // strip should never have *zero* highlighted columns, since the
    // map + per-card UI both anchor to a specific day.
    setSelectedDay(dayOffset);
  };
  // Currently selected spot id (controlled by SpotsMap marker clicks and
  // the MapSpotCard's prev/next chevrons). Always starts null so re-entry
  // doesn't auto-open the previously-selected card.
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null);
  // Currently hovered spot in the LIST — desktop affordance: pointer on a
  // SpotCard scales the corresponding map marker (1.2×) so the user sees
  // where on the map the card sits. Mobile devices don't fire hover, so
  // this stays null in touch contexts (harmless).
  const [hoveredSpotId, setHoveredSpotId] = useState<string | null>(null);
  // Dropped pin from a long-press on the map. Mutually exclusive with
  // `selectedSpotId` — picking either clears the other.
  const [customLocation, setCustomLocation] = useState<Coords | null>(null);
  const { data: customSpot } = useCustomLocationForecast(customLocation);

  const spotListRef = useRef<HTMLDivElement>(null);
  const flipCaptured = useRef<Map<string, number>>(new Map());
  const flipRafRef = useRef<number>(0);
  const flipCleanupRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  function captureFlipPositions() {
    const list = spotListRef.current;
    if (!list) return;
    flipCaptured.current.clear();
    list.querySelectorAll<HTMLElement>("[data-spotcard-id]").forEach((card) => {
      const id = card.dataset.spotcardId;
      if (id) flipCaptured.current.set(id, card.getBoundingClientRect().top);
    });
  }

  useLayoutEffect(() => {
    const list = spotListRef.current;
    if (!list || !flipCaptured.current.size) return;

    const toAnimate: Array<{ el: HTMLElement; delta: number }> = [];
    list.querySelectorAll<HTMLElement>("[data-spotcard-id]").forEach((card) => {
      const id = card.dataset.spotcardId;
      if (!id) return;
      const oldTop = flipCaptured.current.get(id);
      if (oldTop === undefined) return;
      const delta = oldTop - card.getBoundingClientRect().top;
      if (Math.abs(delta) > 0.5) toAnimate.push({ el: card, delta });
    });

    flipCaptured.current.clear();
    if (!toAnimate.length) return;

    cancelAnimationFrame(flipRafRef.current);
    clearTimeout(flipCleanupRef.current);
    toAnimate.forEach(({ el }) => {
      el.style.transition = "";
      el.style.transform = "";
    });

    toAnimate.forEach(({ el, delta }) => {
      el.style.transition = "none";
      el.style.transform = `translateY(${delta}px)`;
    });

    flipRafRef.current = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toAnimate.forEach(({ el }) => {
          el.style.transition = "transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
          el.style.transform = "";
        });
      });
    });

    flipCleanupRef.current = setTimeout(() => {
      toAnimate.forEach(({ el }) => {
        el.style.transition = "";
      });
    }, 270);
  }, [selectedDay, sortMode]);

  const region = regionById(params.regionId as Region);

  useMeta(
    region
      ? {
          title: `${region.label} wind forecast — Big Wind`,
          description: `Spots and 7-day wind forecasts in ${region.label} for kiters, wingfoilers and windsurfers.`,
          path: `/region/${params.regionId}`,
        }
      : { path: `/region/${params.regionId}` },
  );

  // Spots filtered to the active region (unsorted — the sections memo
  // below applies the per-mode ordering and section grouping).
  const regionSpots = useMemo(
    () => (data ? data.spots.filter((s) => s.region === params.regionId) : null),
    [data, params.regionId],
  );

  const sections = useMemo<SpotSection[] | null>(() => {
    if (!regionSpots) return null;
    return buildSections(regionSpots, sortMode, selectedDay);
  }, [regionSpots, sortMode, selectedDay]);

  // Persist filter snapshot continuously while on the page. On a future
  // mount, `readRestoredFilter` will read this back only if the user is
  // coming back from a spot detail — other re-entries get the defaults.
  useEffect(() => {
    writePersistedFilter(params.regionId, { sortMode, selectedDay });
  }, [params.regionId, sortMode, selectedDay]);

  // Flat, deduplicated spot list (in display order) for callers that
  // don't care about sections — selected-spot lookup, map marker render,
  // MapDayTag, etc. The "best-per-day" mode intentionally repeats a
  // spot across multiple day sections; the map only needs each physical
  // spot once, so we keep the first appearance.
  const spots = useMemo<EnrichedSpot[] | null>(() => {
    if (!sections) return null;
    const seen = new Set<string>();
    const out: EnrichedSpot[] = [];
    for (const section of sections) {
      for (const spot of section.spots) {
        if (seen.has(spot.id)) continue;
        seen.add(spot.id);
        out.push(spot);
      }
    }
    return out;
  }, [sections]);

  // Mode change always resets the strip to a sensible default:
  //   • best-week → auto-highlight the global peak day so the column
  //     the section header names ("Best Wednesday 9th") is pinned.
  //   • Anything else → reset to today (0). This clears any strip
  //     selection the user made while in best-today (e.g. tapped Sat
  //     so the dropdown said "Best wind Saturday 23rd"). Without
  //     this reset the dropdown's best-today option would still say
  //     "Best wind Saturday 23rd" after the user switched away, which
  //     reads as a stale selection.
  const handleSortChange = (next: SortMode) => {
    captureFlipPositions();
    setSortMode(next);
    if (next === "best-week" && regionSpots) {
      const peak = globalPeakDayIndex(regionSpots);
      if (peak !== null) setSelectedDay(peak);
      return;
    }
    setSelectedDay(0);
  };

  // Static metadata fallback used to render the partial-skeleton list
  // while the forecast is in flight. No forecast scores yet, so day-based
  // sorts collapse to alphabetical here — keeps the placeholder order
  // stable and avoids reshuffling when the real data swaps in. Bundled
  // with the app — no network wait. See docs/decisions.md "One wind
  // data pipeline for every location": this surfaces only the static
  // bits (area, name, ideal dirs); the forecast row still comes from
  // /api/forecast.
  const staticSpots = useMemo(() => {
    const filtered = STATIC_SPOTS.filter((s) => s.region === params.regionId);
    if (sortMode === "by-area") {
      return [...filtered].sort((a, b) => {
        const areaDiff = a.area.localeCompare(b.area);
        return areaDiff !== 0 ? areaDiff : a.name.localeCompare(b.name);
      });
    }
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [params.regionId, sortMode]);

  // Resolve the selected spot for the MapSpotCard. The card's chevrons
  // scroll its own forecast strip (handled inside MapSpotCard) — they no
  // longer cycle between spots, so no neighbour resolution needed here.
  const selectedSpot = useMemo(
    () => (selectedSpotId ? (spots?.find((s) => s.id === selectedSpotId) ?? null) : null),
    [spots, selectedSpotId],
  );

  // Error / dead-end pages drop the unit toggle + search — there's no
  // forecast or list to act on — and show just a back button.
  if (isError) {
    return (
      <div className={styles.overviewLayout}>
        <div className={styles.navbarSticky}>
          <div className={styles.contentColumn}>
            <Navbar variant="back-only" onBack={() => setLocation("/")} />
          </div>
        </div>
        <div className={styles.contentColumn}>
          <ErrorState
            message="Couldn't load the forecast — probably us, not you. Give it another shot."
            onAction={() => refetch()}
          />
        </div>
      </div>
    );
  }

  if (!region) {
    return (
      <div className={styles.overviewLayout}>
        <div className={styles.navbarSticky}>
          <div className={styles.contentColumn}>
            <Navbar variant="back-only" onBack={() => setLocation("/")} />
          </div>
        </div>
        <div className={styles.contentColumn}>
          <ErrorState
            message="Can't find that region."
            actionLabel="Back to regions"
            onAction={() => setLocation("/")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overviewLayout}>
      <div className={styles.navbarSticky}>
        <div className={styles.contentColumn}>
          <Navbar
            variant="spots"
            title={region.label}
            onBack={() => setLocation("/")}
            unit={unit}
            onCycleUnit={cycleUnit}
            onOpenSearch={() => setSearchOpen(true)}
          />
        </div>
      </div>
      <div
        className={[
          styles.splitWrap,
          (selectedSpotId !== null || customLocation !== null) && styles.hasSelection,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={styles.mapWrap}>
          <div className={styles.mapInner}>
            <MapDayTag selectedDay={selectedDay} date={spots?.[0]?.days[selectedDay ?? 0]?.date} />
            {spots && spots.length > 0 ? (
              <SpotsMap
                spots={spots}
                regionId={params.regionId as Region}
                selectedDay={selectedDay ?? 0}
                selectedSpotId={selectedSpotId}
                hoveredSpotId={hoveredSpotId}
                onSelectSpot={(id) => {
                  setSelectedSpotId(id);
                  if (id !== null) setCustomLocation(null);
                }}
                customLocation={customLocation}
                onLongPressMap={(coords) => {
                  setCustomLocation(coords);
                  setSelectedSpotId(null);
                }}
              />
            ) : (
              <MapSkeleton />
            )}
            {selectedSpot && !customLocation && (
              <MapSpotCard
                spot={selectedSpot}
                unit={unit}
                onClose={() => setSelectedSpotId(null)}
                {...(selectedDay !== null && { selectedDay })}
              />
            )}
            {customLocation &&
              (customSpot ? (
                <MapSpotCard
                  spot={customSpot}
                  unit={unit}
                  customLocation
                  onClose={() => setCustomLocation(null)}
                  {...(selectedDay !== null && { selectedDay })}
                />
              ) : (
                <SkeletonMapSpotCard
                  coords={customLocation}
                  skeletonCount={SKELETON_COUNT}
                  onClose={() => setCustomLocation(null)}
                />
              ))}
          </div>
        </div>
        <MapDrawer className={styles.drawerSlot} footer={<Footer />}>
          <ScrollSyncProvider>
            <div className={styles.dayStripSticky}>
              <DaySelector
                days={spots?.[0]?.days}
                skeletonCount={SKELETON_COUNT}
                selectable
                {...(selectedDay !== null && { selectedDay })}
                onSelectDay={handleSelectDay}
              />
            </div>
            <div className={styles.sortRow}>
              <div className={styles.sortDropdown}>
                <select
                  aria-label="Sort spots"
                  className={styles.sortSelect}
                  value={sortMode}
                  onChange={(e) => handleSortChange(e.target.value as SortMode)}
                >
                  <option value="best-today">
                    {/* When best-today is the active sort, the label
                        reflects the strip's selected day (e.g. "Best
                        wind Saturday 23rd"). When another mode is
                        active, fall back to "Best wind today" so the
                        option always describes what best-today *would*
                        do — not whatever leftover day-strip selection
                        another mode happens to have. */}
                    {bestTodayDropdownLabel(
                      sortMode === "best-today" ? selectedDay : 0,
                      regionSpots?.[0]?.days[sortMode === "best-today" ? (selectedDay ?? 0) : 0]
                        ?.date,
                    )}
                  </option>
                  <option value="best-week">Best wind this week</option>
                  <option value="by-area">By area</option>
                  <option value="alpha">Spots A–Z</option>
                </select>
                <Icon name="chevron-down" className={styles.sortChevron} />
              </div>
            </div>
            <div className={styles.spotList} ref={spotListRef}>
              {sections ? (
                sections.length > 0 ? (
                  sections.map((section, sectionIdx) => (
                    <Fragment key={sectionIdx}>
                      {section.header && (
                        <ListHeader className={styles.sortSectionHeader}>
                          {section.header}
                        </ListHeader>
                      )}
                      {section.spots.map((spot) => {
                        return (
                          <SpotCard
                            key={`${sectionIdx}-${spot.id}`}
                            spot={spot}
                            unit={unit}
                            selected={spot.id === selectedSpotId}
                            {...(selectedDay !== null && { selectedDay })}
                            onHoverChange={(hovered) => setHoveredSpotId(hovered ? spot.id : null)}
                          />
                        );
                      })}
                    </Fragment>
                  ))
                ) : (
                  <p className={styles.empty}>No spots in {region.label} yet.</p>
                )
              ) : staticSpots.length > 0 ? (
                staticSpots.map((spot) => (
                  <SkeletonSpotCard key={spot.id} skeletonCount={SKELETON_COUNT} spot={spot} />
                ))
              ) : (
                Array.from({ length: 5 }, (_, i) => (
                  <SkeletonSpotCard key={i} skeletonCount={SKELETON_COUNT} />
                ))
              )}
            </div>
          </ScrollSyncProvider>
        </MapDrawer>
      </div>
      <div className={styles.xlFooter}>
        <Footer />
      </div>

      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        spots={data?.spots ?? []}
        unit={unit}
      />
    </div>
  );
}
